<template>
  <div class="bg-gray-600 mb-2 p-2 pr-0 rounded-8px">
    <div class="grid grid-cols-[72px_19fr_19fr_12fr_96px_5fr] xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px] gap-x-4 text-14px leading-5">
      <div class="cover-image w-[72px] h-[72px] rounded-8px overflow-hidden flex items-center justify-center">
        <img :src="campaign.cover_image_src" :alt="campaign.title" class="w-full h-full object-cover object-center" />
      </div>
      <div class='title flex items-center text-12px xl:text-14px font-medium hover:underline break-word'>
        <a :href="campaign.state === 'published' ? `/campaigns/${campaign.id}#campaign` : `/campaigns/${campaign.id}/preview`">
          {{ campaign.title }}
        </a>
      </div>
      <div class='created-at flex xl:hidden items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(campaign.start_at) }}
        <br>
        {{ formatDate(campaign.end_at) }}
      </div>
      <div class='created-at hidden xl:flex items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(campaign.created_at) }}
        <br>
        {{ formatDate(campaign.updated_at) }}
      </div>
      <div class='updated-at hidden xl:flex items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(campaign.start_at) }}
        <br>
        {{ formatDate(campaign.end_at) }}
      </div>
      <div class='state flex flex-col justify-center items-center xl:flex-row xl:items-center xl:justify-between'>
        <div class='state-text'>
          <span class="inline-block py-1 px-2 rounded-2xl border"
                :style="{ color: `var(--color-post-state-${campaign.state.replace(/_/g, '-')})`,
                      background: `color-mix(in srgb, var(--color-post-state-${campaign.state.replace(/_/g, '-')}), transparent 80%)`,
                      borderColor: `var(--color-post-state-${campaign.state.replace(/_/g, '-')})` }">
            {{ $t(`post.state.${campaign.state}`) }}
          </span>
        </div>
        <a v-if="campaign.state === 'draft'"
            href="#"
            class="min-w-14 btn btn-primary mt-2 xl:mt-0 xl:ml-4 py-1 px-2 text-12px max-h-6 rounded-4px"
            @click.prevent="submitAction"
            v-bind="isSubmitting ? { disabled: true } : {}">
            {{ isSubmitting ? $t('post.state.pending') : $t('models.index.submit') }}
        </a>
      </div>
      <div class='state-icon flex justify-end items-center'>
        <a v-if="campaign.state === 'published'" :href="dashboardLink"
            class="edit-btn flex justify-center items-center w-8 h-8 rounded-full
                    filter transition-filter duration-100 ease-in-out
                    hover:[filter:invert(50%)_sepia(10%)_saturate(500%)_hue-rotate(200deg)_brightness(90%)_contrast(90%)]">
          <img src="../../images/dashboard.svg" class="w-4.5 h-4.5 object-contain" />
        </a>
        <a :href="editLink"
            class="edit-btn flex justify-center items-center w-8 h-8 rounded-full
                    filter transition-filter duration-100 ease-in-out
                    hover:[filter:invert(50%)_sepia(10%)_saturate(500%)_hue-rotate(200deg)_brightness(90%)_contrast(90%)]">
          <img src="../../images/edit.svg" class="w-8 h-8 object-contain" />
        </a>
        <a href="#" class="delete-btn flex justify-center items-center w-8 h-8 rounded-full
                            filter transition-filter duration-100 ease-in-out
                            hover:[filter:invert(50%)_sepia(10%)_saturate(500%)_hue-rotate(200deg)_brightness(90%)_contrast(90%)]"
            @click.prevent="confirmDelete">
            <img src="../../images/trash.svg" class="w-8 h-8 object-contain" />
        </a>
      </div>
      <div class='py-2 flex justify-center items-center'>
        <span class="variant-collapse-btn cursor-pointer">
          <div class="w-4 h-4 font-bold text-16px px-4 collapse-btn"
               :class="{ 'checked': variantExpanded }"
               @click="toggleVariant()"></div>
        </span>
      </div>
    </div>

    <!-- 展開區域：進度條和 Rewards -->
    <div v-if="variantExpanded">
      <hr class='border-grey-3 mt-2 mb-2 mr-2 col-span-6 border-gray-200'>

      <!-- 進度條區域 -->
      <div v-if="campaign.rewards && campaign.state === 'published'" class="mr-2 mb-2">
        <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div class="h-full rounded-full transition-all duration-300"
               :class="progressBarClass"
               :style="{ width: `${Math.min(progressPercentage, 100)}%` }">
          </div>
        </div>
        <div class="flex justify-between items-center mt-1 text-12px">
          <span class="font-medium">{{ progressPercentage.toFixed(1) }}%</span>
          <div class="flex items-center gap-x-2">
            <span class="text-gray-200">
              {{ campaign.backers_count || 0 }} {{ $t('campaign_models.index.backers', { default: 'backers' }) }}
            </span>
            <span class="font-medium">
              {{ formatCurrency(campaign.current_amount || 0) }} / {{ formatCurrency(campaign.goal_price || 0) }}
            </span>
            <span v-if="campaign.days_left > 0" class="font-medium">
              <span class="text-primary">
                {{ campaign.days_left || 0 }}
              </span>
              {{ $t('campaign_models.index.days_left', { default: 'days left' }) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Rewards 列表 -->
      <div v-if="campaign.rewards && campaign.rewards.length > 0">
        <div class="grid
                    grid-cols-[72px_19fr_19fr_12fr_96px_5fr]
                    xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px]
                    gap-x-4
                    gap-y-1
                    text-12px text-gray-200">
          <div class=''></div>
          <div class='h-5 text-grey-3 font-medium'>{{ $t('campaign_models.index.reward_title', { default: 'Reward' }) }}</div>
          <div class='h-5 text-grey-3 font-medium'>{{ $t('campaign_models.index.reward_price', { default: 'Price' }) }}</div>
          <div class='h-5 text-grey-3 font-medium'>{{ $t('campaign_models.index.reward_sold', { default: 'Sold' }) }}</div>
          <div class='h-5 text-grey-3 font-medium'>{{ $t('campaign_models.index.reward_delivery', { default: 'Est. Delivery' }) }}</div>
        </div>
        <div class="grid
                    grid-cols-[72px_19fr_19fr_12fr_96px_5fr]
                    xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px]
                    gap-x-4
                    gap-y-1
                    text-12px
                    xl:text-14px">
          <template v-for="reward in campaign.rewards" :key="reward.id">
            <div class="name truncate h-5 col-span-2" :title="reward.title">
              {{ reward.title }}
            </div>
            <div class="price h-5">
              {{ formatCurrency(reward.price) }}
            </div>
            <div class="sold h-5">
              {{ reward.sold_quantity || 0 }}
            </div>
            <div class="delivery h-5 col-span-2 xl:col-span-3">
              {{ formatShortDate(reward.estimated_delivery_at) }}
            </div>
          </template>
        </div>
      </div>

      <div v-else class="text-center text-gray-200 text-12px py-2 mr-2">
        {{ $t('campaign_models.index.no_rewards', { default: 'No rewards yet' }) }}
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';

export default defineComponent({
  props: {
    campaign: {
      type: Object,
      required: true
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  emits: ['campaign-submit', 'campaign-deleted'],
  setup(props, { emit }) {
    const variantExpanded = ref(false);

    const editLink = computed(() => {
      return `/profile/campaigns/${props.campaign.id}/edit`;
    });

    const dashboardLink = computed(() => {
      return `/profile/campaigns/${props.campaign.id}/dashboard`;
    });

    const progressPercentage = computed(() => {
      if (!props.campaign.goal_price || props.campaign.goal_price === 0) return 0;
      return ((props.campaign.current_amount || 0) / props.campaign.goal_price) * 100;
    });

    const progressBarClass = computed(() => {
      const pct = progressPercentage.value;
      if (pct >= 100) return 'bg-green-500';
      if (pct >= 75) return 'bg-blue-500';
      if (pct >= 50) return 'bg-yellow-500';
      return 'bg-primary';
    });

    const toggleVariant = () => {
      variantExpanded.value = !variantExpanded.value;
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatShortDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    const submitAction = async () => {
      if (props.isSubmitting) return;
      emit('campaign-submit', props.campaign.id, props.campaign.state);
    };

    const confirmDelete = async () => {
      if (confirm("Are you sure you want to delete this campaign?")) {
        try {
          const response = await fetch(`/profile/campaigns/${props.campaign.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            }
          });

          const result = await response.json();

          if (response.ok && result.success) {
            emit('campaign-deleted', props.campaign);
          } else {
            throw new Error(result.message || 'Failed to delete campaign');
          }
        } catch (error) {
          console.error('Error deleting campaign:', error);
          alert(error.message);
        }
      }
    };

    return {
      variantExpanded,
      dashboardLink,
      editLink,
      progressPercentage,
      progressBarClass,
      toggleVariant,
      formatDate,
      formatShortDate,
      formatCurrency,
      submitAction,
      confirmDelete
    };
  }
});
</script>
